import type { FormEvent } from "react";
import bigLogo from "@/assets/big-logo.png";

interface ClientPortalLoginProps {
  email: string;
  password: string;
  error: string | null;
  submitting: boolean;
  resetting: boolean;
  resetSent: boolean;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
  onGoogleSignIn: () => void;
  onPasswordReset: () => void;
}

export function ClientPortalLogin({
  email,
  password,
  error,
  submitting,
  resetting,
  resetSent,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  onGoogleSignIn,
  onPasswordReset,
}: ClientPortalLoginProps) {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#f7f3eb] px-4 py-10">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute -right-32 -top-36 h-[32rem] w-[32rem] rounded-full bg-[#dcece1]" />
        <div className="absolute -bottom-28 -left-20 h-80 w-80 rounded-full bg-[#f2dfda]" />
      </div>

      <main className="relative w-full max-w-md">
        <div className="mb-6 text-center">
          <img src={bigLogo} alt="BiG Impact Group" className="mx-auto mb-5 h-12" />
          <p className="font-body text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#277454]">
            BiG Impact Group
          </p>
          <h1 className="mt-2 font-heading text-4xl font-bold text-[#17221c]">Client Portal</h1>
          <p className="mx-auto mt-2 max-w-sm font-body text-sm leading-6 text-[#5f6963]">
            Sign in to manage your organization, workspaces, users, and resources.
          </p>
        </div>

        <section className="rounded-2xl border border-[#d9ded8] bg-[#fffefb] p-6 shadow-[0_18px_50px_rgba(23,79,57,0.09)] sm:p-8">
          <div className="mb-6 rounded-lg border border-[#bad0c0] bg-[#f0f7f2] px-4 py-3">
            <p className="font-body text-xs leading-5 text-[#385344]">
              Organization owners and administrators use invited accounts. BiG platform team members can continue with Google.
            </p>
          </div>

          {error ? (
            <div role="alert" className="mb-4 rounded-lg border border-[#e4b8af] bg-[#fff3f0] p-3 font-body text-sm text-[#9e4034]">
              {error}
            </div>
          ) : null}

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="mb-1.5 block font-body text-xs font-bold text-[#26352d]">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => onEmailChange(event.target.value)}
                disabled={submitting}
                placeholder="you@company.com"
                className="min-h-11 w-full rounded-lg border border-[#ccd5ce] bg-white px-3 font-body text-sm text-[#17221c] outline-none transition focus:border-[#277454] focus:ring-2 focus:ring-[#277454]/15 disabled:opacity-60"
              />
            </div>

            <div>
              <label htmlFor="password" className="mb-1.5 block font-body text-xs font-bold text-[#26352d]">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => onPasswordChange(event.target.value)}
                disabled={submitting}
                placeholder="Enter your password"
                className="min-h-11 w-full rounded-lg border border-[#ccd5ce] bg-white px-3 font-body text-sm text-[#17221c] outline-none transition focus:border-[#277454] focus:ring-2 focus:ring-[#277454]/15 disabled:opacity-60"
              />
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={onPasswordReset}
                  disabled={submitting || resetting}
                  className="font-body text-xs font-bold text-[#277454] hover:text-[#174f39] disabled:opacity-50"
                >
                  {resetting ? "Sending..." : "Forgot password?"}
                </button>
              </div>
            </div>

            {resetSent ? (
              <p className="font-body text-sm leading-5 text-[#277454]" role="status">
                If an account exists for that address, a reset email is on its way. Check your spam folder for mail from noreply@studio-manager-c68eb.firebaseapp.com.
              </p>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              className="min-h-11 w-full rounded-lg bg-[#174f39] px-4 font-heading text-sm font-bold text-white transition hover:bg-[#277454] focus:outline-none focus:ring-2 focus:ring-[#e66f51]/50 focus:ring-offset-2 disabled:opacity-50"
            >
              {submitting ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#e0e5df]" /></div>
            <div className="relative flex justify-center"><span className="bg-[#fffefb] px-3 font-body text-[10px] font-bold uppercase tracking-wider text-[#7b857f]">or</span></div>
          </div>

          <button
            type="button"
            onClick={onGoogleSignIn}
            disabled={submitting}
            className="flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-[#ccd5ce] bg-white px-4 font-body text-sm font-bold text-[#26352d] transition hover:bg-[#f7f3eb] focus:outline-none focus:ring-2 focus:ring-[#277454]/25 disabled:opacity-50"
          >
            <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Continue with Google
          </button>
          <p className="mt-3 text-center font-body text-xs text-[#6d7771]">
            Google sign-in is for BiG Impact Group team members.
          </p>
        </section>

        <p className="mt-5 text-center font-body text-xs text-[#6d7771]">
          Client Portal by BiG Impact Group
        </p>
      </main>
    </div>
  );
}
